<template>
  <Modal
    v-model:visible="isVisible"
    class="shipment-cancel-modal"
    content-class="flex flex-column flex-gap-1"
    :header="
      hasCarrierNotificationError
        ? t('shipments.cancelShipmentFailed')
        : t('common.cancelParam', { param: t('shipments.shipments') })
    "
    @keypress.esc="isVisible = false"
  >
    <Message v-if="hasRelatedInvoices" severity="warn" class="rygen-message">
      <p class="text-bold">
        {{
          t('shipments.financials.countInvoicesAreAssociatedWithThisShipment', {
            count: relatedInvoices.data?.length,
          })
        }}
      </p>
      <ul>
        <li
          v-for="{ invoice, link } in relatedInvoiceLinks"
          :key="invoice.invoiceId"
        >
          <a v-if="link" :href="link" target="_blank">
            {{ invoice.getDescription() }}
          </a>
          <template v-else>
            {{ invoice.getDescription() }}
          </template>
        </li>
      </ul>
      <p>
        {{ t('shipments.financials.cancelingShipmentDoesNotVoidInvoices') }}
      </p>
    </Message>

    <template v-if="hasCarrierNotificationError">
      <h2 class="font-bold">{{ t('errors.errors') }}:</h2>
      <p class="error-formatting">
        {{ carrierNotificationErrorMessage }}
      </p>
      <br />
      <p id="cancel-instructions">
        {{ t('shipments.cancelShipmentAnyways') }}
      </p>
    </template>
    <p v-else-if="requiresManualCancellation" id="cancel-instructions">
      {{ t('shipments.confirmManualCancelation') }}
    </p>
    <p v-else id="cancel-instructions">
      {{ t('shipments.confirmCancelation') }}
    </p>

    <form
      id="cancel-form"
      aria-labelledby="cancel-instructions"
      class="cancellation-reason-form"
    >
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${uid}-reason`"
        :errors="v$.cancellationReason.id.$errors"
        :label="t('orders.cancellationReason')"
      >
        <RygenDropdown
          :id="inputId"
          v-model="internalValue.cancellationReason"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="cancellationReasons.data"
          :loading="cancellationReasons.loading"
          option-label="name"
          append-to="body"
          filter
          :placeholder="t('orders.cancellationReason')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${uid}-comments`"
        :label="t('common.comments', 2)"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="internalValue.cancellationComments"
          v-bind="ariaProps"
          :placeholder="t('common.comments', 2)"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="
          t('common.dontCancelParam', {
            param: t('shipments.shipments'),
          })
        "
        @click="isVisible = false"
      />

      <RygenButton
        v-if="hasCarrierNotificationError"
        v-tooltip="{
          value: t(
            'shipments.financials.cancelInvoicedShipmentPermissionRequired',
          ),
          disabled: !showNotAuthorizedToCancelInvoicedShipmentTooltip,
        }"
        :disabled="!isAuthorizedToPerformCancel"
        icon="pi pi-check"
        :label="t('shipments.cancelShipmentWithoutInformingCarrier')"
        :loading="isLoading"
        severity="danger"
        @click="onCancelWithoutInformingCarrier"
      />

      <RygenButton
        v-else
        v-tooltip="{
          value: t(
            'shipments.financials.cancelInvoicedShipmentPermissionRequired',
          ),
          disabled: !showNotAuthorizedToCancelInvoicedShipmentTooltip,
        }"
        :label="t('common.cancelParam', { param: t('shipments.shipments') })"
        :disabled="!isAuthorizedToPerformCancel"
        icon="pi pi-check"
        :loading="isLoading"
        severity="danger"
        @click="onCancel"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { usePrimeVue } from 'primevue/config';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  required,
  useBillingPermissions,
  useToast,
  useTooltip,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';
import { ShipmentFinancialsPerspective } from '@rygen/rygen-ui/model/shipmentFinancials';

import api from '@/api';
import { useShipmentFinancials } from '@/composables';
import {
  CancellationReason,
  CancelShipmentPayload,
  Shipment,
  ShipmentRelatedInvoiceReference,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { generateCarrierInvoiceLink } from '@/util/CorsairSuiteLinkUtil';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Message from 'primevue/message';
import Textarea from 'primevue/textarea';

type RelatedInvoiceLink = {
  invoice: ShipmentRelatedInvoiceReference;
  link: string | undefined;
};

defineExpose({
  $primevue: usePrimeVue(),
});

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipment: {
    type: Shipment,
    required: true,
  },
  carrierNotificationErrorMessage: {
    type: String,
    default: () => undefined,
  },
  requiresManualCancellation: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const {
  shipment,
  carrierNotificationErrorMessage,
  requiresManualCancellation,
  loading,
} = toRefs(props);
const emit = defineEmits<{
  (e: 'cancel-shipment', payload: CancelShipmentPayload): void;
}>();

const { hasCarrierInvoiceReadPermission } = useBillingPermissions();
const { selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { shipmentFinancials } = useShipmentFinancials(shipment);
const toast = useToast();
const vTooltip = useTooltip();
const { uid } = useUid('cancel-shipment-modal');

const internalValue: Ref<Partial<CancelShipmentPayload>> = ref({
  cancellationReason: undefined,
  cancellationComments: '',
});

const rules = {
  cancellationReason: {
    id: {
      required: required(t('orders.cancellationReason')),
    },
  },
};
const { v$, validate } = useValidations(internalValue, rules, uid);

const cancellationReasons: Ref<Loadable<CancellationReason[]>> = ref({
  loading: false,
});

const relatedInvoices: Ref<Loadable<ShipmentRelatedInvoiceReference[]>> = ref({
  loading: false,
});

const isLoading = computed(
  () => loading.value || relatedInvoices.value.loading,
);

const hasCarrierNotificationError = computed(
  () => !!carrierNotificationErrorMessage?.value?.length,
);

const hasRelatedInvoices = computed(() => !!relatedInvoices.value.data?.length);

// only carrier invoices can be manually voided, so those are the only ones worth linking
const canLinkToCarrierInvoice = (invoice: ShipmentRelatedInvoiceReference) => {
  return (
    invoice.type === 'CARRIER_INVOICE' &&
    shipmentFinancials.value.data?.perspective !==
      ShipmentFinancialsPerspective.THIRD_PARTY_LOGISTICS_PROVIDER_CLIENT &&
    hasCarrierInvoiceReadPermission.value
  );
};

const relatedInvoiceLinks = computed<RelatedInvoiceLink[]>(() =>
  (relatedInvoices.value.data ?? []).map((invoice) => ({
    invoice,
    link: canLinkToCarrierInvoice(invoice)
      ? generateCarrierInvoiceLink(invoice, selectedDomain.value?.naturalId)
      : undefined,
  })),
);

const showNotAuthorizedToCancelInvoicedShipmentTooltip = computed(
  () => !isAuthorizedToPerformCancel.value && hasRelatedInvoices.value,
);

const isAuthorizedToPerformCancel = computed(() => {
  if (!hasRelatedInvoices.value) {
    return authorization.hasAuthority('shipment', 'cancel');
  }

  return authorization.hasAuthority('shipment', 'cancelInvoicedShipment');
});

const loadRelatedInvoices = async () => {
  if (!shipment.value.id) {
    relatedInvoices.value.data = [];
    return;
  }

  try {
    relatedInvoices.value.loading = true;
    const shipmentInvoices =
      await api.shipmentFinancials.getShipmentInvoicesById(shipment.value.id);

    relatedInvoices.value.data =
      shipmentInvoices.filter((invoice) => !invoice.isVoid()) ?? [];
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Failed to load shipment invoices', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('shipments.financials.financials', 2),
      }),
      error: e,
    });
  } finally {
    relatedInvoices.value.loading = false;
  }
};

const loadCancellationReasons = async () => {
  try {
    if (cancellationReasons.value.data) {
      return;
    }

    cancellationReasons.value.loading = true;
    cancellationReasons.value.data =
      await api.shipmentCancellationReason.findAll();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    cancellationReasons.value.error = e;
    console.error('Error loading cancellation reasons', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.cancellationReason'),
      }),
      error: e,
    });
  } finally {
    cancellationReasons.value.loading = false;
  }
};

const resetState = () => {
  internalValue.value = {
    cancellationReason: undefined,
    cancellationComments: '',
  };
  v$.value.$reset();
};

const onCancelWithoutInformingCarrier = async () => {
  if (await validate()) {
    // validations ensure `cancellationReason` is defined
    emit('cancel-shipment', {
      cancellationReason: internalValue.value.cancellationReason!,
      cancellationComments: internalValue.value.cancellationComments,
      sendUpdateToCarrier: false,
    });
    isVisible.value = false;
  }
};

const onCancel = async () => {
  if (await validate()) {
    // validations ensure fields is defined
    emit('cancel-shipment', {
      cancellationReason: internalValue.value.cancellationReason!,
      cancellationComments: internalValue.value.cancellationComments,
    });
  }
};

watch(
  isVisible,
  (newVal) => {
    if (newVal) {
      resetState();
      loadCancellationReasons();
      loadRelatedInvoices();
    }
  },
  { immediate: true },
);
</script>

<style>
.shipment-cancel-modal {
  .p-message {
    margin-bottom: var(--sp-3);
  }

  .error-formatting {
    font-family: monospace;
    font-size: small;
  }

  .cancellation-reason-form {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
    margin-top: var(--sp-2);
  }
}
</style>
